/*
 * 图标。
 *
 * 手写 SVG 而不是引图标库：旧项目同时装了 @ant-design/icons 与
 * antd-mobile-icons，两套线宽和视觉重心不同，同一行里混着用（例如
 * CharacterRelationDiaryModule 同时出现 HeartOutline 与 HeartTwoTone）。
 * 这里统一 24 视框、1.6 线宽、currentColor 描边，尺寸由字号驱动。
 */

interface IconProps {
  size?: number;
  className?: string;
}

const base = (size: number) => ({
  width: size,
  height: size,
  viewBox: "0 0 24 24",
  fill: "none" as const,
  stroke: "currentColor" as const,
  strokeWidth: 1.6,
  strokeLinecap: "round" as const,
  strokeLinejoin: "round" as const,
  "aria-hidden": true,
  focusable: false as const,
});

export function IconChat({ size = 20, className }: IconProps) {
  return (
    <svg {...base(size)} className={className}>
      <path d="M20 12a7.5 7.5 0 0 1-7.5 7.5H8l-4 2.5v-4.2A7.5 7.5 0 0 1 12.5 4.5 7.5 7.5 0 0 1 20 12Z" />
    </svg>
  );
}

export function IconHistory({ size = 20, className }: IconProps) {
  return (
    <svg {...base(size)} className={className}>
      <path d="M3.5 12a8.5 8.5 0 1 0 2.6-6.1" />
      <path d="M3.5 4.5V9H8" />
      <path d="M12 7.8V12l3 1.8" />
    </svg>
  );
}

export function IconMemory({ size = 20, className }: IconProps) {
  return (
    <svg {...base(size)} className={className}>
      <path d="M5 4.5h9.5A2.5 2.5 0 0 1 17 7v13H7.5A2.5 2.5 0 0 1 5 17.5Z" />
      <path d="M17 7h1.5A1.5 1.5 0 0 1 20 8.5V20" />
      <path d="M8.2 9h5.6M8.2 12.5h4" />
    </svg>
  );
}

export function IconHeart({ size = 20, className }: IconProps) {
  return (
    <svg {...base(size)} className={className}>
      <path d="M12 20s-7.2-4.3-7.2-9.2A4.1 4.1 0 0 1 12 8.3a4.1 4.1 0 0 1 7.2 2.5C19.2 15.7 12 20 12 20Z" />
    </svg>
  );
}

export function IconUser({ size = 20, className }: IconProps) {
  return (
    <svg {...base(size)} className={className}>
      <circle cx="12" cy="8.5" r="3.6" />
      <path d="M4.8 20a7.4 7.4 0 0 1 14.4 0" />
    </svg>
  );
}

/**
 * 齿轮。
 *
 * ⚠ 这里曾经是**一个圆加八条放射短线**——那是太阳，不是齿轮。
 * 区别在于齿是圆周上有宽度的梯形凸起，不是从圆心射出去的线。
 *
 * 六齿而不是八齿：齿数是由**渲染尺寸**定的，不是审美。这个图标画在 20px
 * （页面标题）与 18px（右上角图标栏）上，线宽 1.6。八齿时齿间空隙的弦长
 * 只有 2.4 视框单位 ≈ 2.0 实际像素，两侧描边各占 1.6，空隙糊成一团；
 * 六齿是 3.3 单位 ≈ 2.7 像素，槽口还看得见。
 *
 * 几何是算出来的（齿根圆 r=7.3，齿顶圆 r=9.8，齿顶半角 11°、齿根半角 17°，
 * 六齿 60° 均分），**坐标不要手改**：改一个点齿距就不均匀，
 * 而在 20px 上不均匀齿距一眼可见。要调就整条重算。
 *
 * 中间 r=3.1 的轴孔不能省——省了就成了一朵花。
 */
export function IconSettings({ size = 20, className }: IconProps) {
  return (
    <svg {...base(size)} className={className}>
      <path d="M9.9 5L10.1 2.4L13.9 2.4L14.1 5A7.3 7.3 0 0 1 17 6.7L19.4 5.6L21.3 8.8L19.1 10.4A7.3 7.3 0 0 1 19.1 13.6L21.3 15.2L19.4 18.4L17 17.3A7.3 7.3 0 0 1 14.1 19L13.9 21.6L10.1 21.6L9.9 19A7.3 7.3 0 0 1 7 17.3L4.6 18.4L2.7 15.2L4.9 13.6A7.3 7.3 0 0 1 4.9 10.4L2.7 8.8L4.6 5.6L7 6.7A7.3 7.3 0 0 1 9.9 5Z" />
      <circle cx="12" cy="12" r="3.1" />
    </svg>
  );
}

export function IconSend({ size = 20, className }: IconProps) {
  return (
    <svg {...base(size)} className={className}>
      <path d="M4.4 11.8 20 4.4l-7.4 15.6-1.9-6.3z" />
      <path d="m10.7 13.7 9.3-9.3" />
    </svg>
  );
}

export function IconRetry({ size = 20, className }: IconProps) {
  return (
    <svg {...base(size)} className={className}>
      <path d="M20 12a8 8 0 1 1-2.5-5.8" />
      <path d="M20.5 3.5V9H15" />
    </svg>
  );
}

export function IconClose({ size = 20, className }: IconProps) {
  return (
    <svg {...base(size)} className={className}>
      <path d="m6.5 6.5 11 11M17.5 6.5l-11 11" />
    </svg>
  );
}

export function IconBack({ size = 20, className }: IconProps) {
  return (
    <svg {...base(size)} className={className}>
      <path d="M14.5 5.5 8 12l6.5 6.5" />
    </svg>
  );
}

/** 向右的角括号。与 IconBack 成对，视觉小说模式翻页用。 */
export function IconForward({ size = 20, className }: IconProps) {
  return (
    <svg {...base(size)} className={className}>
      <path d="M9.5 5.5 16 12l-6.5 6.5" />
    </svg>
  );
}

export function IconOffline({ size = 20, className }: IconProps) {
  return (
    <svg {...base(size)} className={className}>
      <path d="M2.5 8.6A15.6 15.6 0 0 1 8 5.5M22 9.2a15.7 15.7 0 0 0-5.6-3.4" />
      <path d="M6 12.4a10.4 10.4 0 0 1 3-1.8M18.7 13a10.5 10.5 0 0 0-3.3-2" />
      <path d="M9.4 16.2a5.2 5.2 0 0 1 5.6.6" />
      <circle cx="12" cy="19.4" r="0.9" fill="currentColor" stroke="none" />
      <path d="m3.5 3.5 17 17" strokeWidth="1.9" />
    </svg>
  );
}

export function IconSpark({ size = 20, className }: IconProps) {
  return (
    <svg {...base(size)} className={className}>
      <path d="M12 3.5c.6 3.6 1.8 5 5.3 5.7-3.5.7-4.7 2.1-5.3 5.7-.6-3.6-1.8-5-5.3-5.7 3.5-.7 4.7-2.1 5.3-5.7Z" />
      <path d="M17.8 15.2c.3 1.8.9 2.5 2.7 2.9-1.8.4-2.4 1.1-2.7 2.9-.3-1.8-.9-2.5-2.7-2.9 1.8-.4 2.4-1.1 2.7-2.9Z" />
    </svg>
  );
}

export function IconPower({ size = 20, className }: IconProps) {
  return (
    <svg {...base(size)} className={className}>
      <path d="M12 3.2c2.6 3 4 5.2 4 7.4a4 4 0 0 1-8 0c0-1 .3-2 .9-3.1.4 1.2 1 1.9 1.8 2.2-.2-2.4.2-4.5 1.3-6.5Z" />
      <path d="M8.6 20.4h6.8" />
    </svg>
  );
}

export function IconInfo({ size = 20, className }: IconProps) {
  return (
    <svg {...base(size)} className={className}>
      <circle cx="12" cy="12" r="8.5" />
      <path d="M12 11v5.2" />
      <circle cx="12" cy="8" r="0.9" fill="currentColor" stroke="none" />
    </svg>
  );
}

export function IconAlert({ size = 20, className }: IconProps) {
  return (
    <svg {...base(size)} className={className}>
      <path d="M12 4.2 21 19.5H3z" />
      <path d="M12 10v4" />
      <circle cx="12" cy="16.8" r="0.9" fill="currentColor" stroke="none" />
    </svg>
  );
}

export function IconImage({ size = 20, className }: IconProps) {
  return (
    <svg {...base(size)} className={className}>
      <rect x="3.5" y="4.8" width="17" height="14.4" rx="2.6" />
      <circle cx="8.8" cy="9.8" r="1.5" />
      <path d="m4.2 16.6 4.6-4.2 3.4 3 2.8-2.3 5 4.3" />
    </svg>
  );
}

export function IconDownload({ size = 20, className }: IconProps) {
  return (
    <svg {...base(size)} className={className}>
      <path d="M12 3.5v11" />
      <path d="m7.8 10.7 4.2 4.2 4.2-4.2" />
      <path d="M4.5 19.5h15" />
    </svg>
  );
}

export function IconZoomIn({ size = 20, className }: IconProps) {
  return (
    <svg {...base(size)} className={className}>
      <circle cx="10.5" cy="10.5" r="6.3" />
      <path d="m15.2 15.2 4.3 4.3M10.5 7.8v5.4M7.8 10.5h5.4" />
    </svg>
  );
}

export function IconZoomOut({ size = 20, className }: IconProps) {
  return (
    <svg {...base(size)} className={className}>
      <circle cx="10.5" cy="10.5" r="6.3" />
      <path d="m15.2 15.2 4.3 4.3M7.8 10.5h5.4" />
    </svg>
  );
}

export function IconMic({ size = 20, className }: IconProps) {
  return (
    <svg {...base(size)} className={className}>
      <rect x="9.2" y="3.2" width="5.6" height="10.4" rx="2.8" />
      <path d="M5.6 11.4a6.4 6.4 0 0 0 12.8 0" />
      <path d="M12 17.8V21" />
    </svg>
  );
}

export function IconMusic({ size = 20, className }: IconProps) {
  return (
    <svg {...base(size)} className={className}>
      <path d="M9.2 18.1V6.2l9.2-2v11.5" />
      <ellipse cx="6.5" cy="18.2" rx="2.7" ry="2" />
      <ellipse cx="15.7" cy="15.8" rx="2.7" ry="2" />
      <path d="M9.2 9.1 18.4 7" />
    </svg>
  );
}

export function IconPlay({ size = 20, className }: IconProps) {
  return (
    <svg {...base(size)} className={className}>
      <path d="m8.2 5.5 10 6.5-10 6.5Z" />
    </svg>
  );
}

export function IconPause({ size = 20, className }: IconProps) {
  return (
    <svg {...base(size)} className={className}>
      <path d="M8.2 5.5v13M15.8 5.5v13" />
    </svg>
  );
}

export function IconStop({ size = 20, className }: IconProps) {
  return (
    <svg {...base(size)} className={className}>
      <rect x="7" y="7" width="10" height="10" rx="1" />
    </svg>
  );
}

export function IconMoon({ size = 20, className }: IconProps) {
  return (
    <svg {...base(size)} className={className}>
      <path d="M20 14.4A8.4 8.4 0 0 1 9.6 4a8.4 8.4 0 1 0 10.4 10.4Z" />
    </svg>
  );
}

/* -------------------------------------------------------------------------- */
/* 舞台图标栏（沿用旧版 antd 原图形）                                          */
/* -------------------------------------------------------------------------- */

/*
 * 这四个是旧版 CharacterChat 的 iconList 用的 antd 图标：
 * CalendarOutlined / HeartOutlined / CommentOutlined / SolutionOutlined。
 *
 * 直接搬 @ant-design/icons-svg 的原始 path，viewBox 1024、fill 填充，
 * 不再自己按 24 视框重画——上一版是我照着"看起来像"手描的，形状对不上，
 * 一眼就能看出不是同一套。
 */

const filled = (size: number) => ({
  width: size,
  height: size,
  viewBox: "0 0 1024 1024",
  fill: "currentColor",
  "aria-hidden": true,
  focusable: false as const,
});

/** 回忆点滴。 */
export function IconCalendar({ size = 18, className }: IconProps) {
  return (
    <svg {...filled(size)} className={className}>
      <path d="M880 184H712v-64c0-4.4-3.6-8-8-8h-56c-4.4 0-8 3.6-8 8v64H384v-64c0-4.4-3.6-8-8-8h-56c-4.4 0-8 3.6-8 8v64H144c-17.7 0-32 14.3-32 32v664c0 17.7 14.3 32 32 32h736c17.7 0 32-14.3 32-32V216c0-17.7-14.3-32-32-32zm-40 656H184V460h656v380zM184 392V256h128v48c0 4.4 3.6 8 8 8h56c4.4 0 8-3.6 8-8v-48h256v48c0 4.4 3.6 8 8 8h56c4.4 0 8-3.6 8-8v-48h128v136H184z" />
    </svg>
  );
}

/*
 * 实心心与实心火焰。
 *
 * 旧版左上角数值面板用的是 `HeartTwoTone` / `FireTwoTone`——**实心**双色图标，
 * 不是描边（`user/MyReputation.tsx:43`、`MyPower.tsx:23`）。这里搬 antd 的
 * `HeartFilled` / `FireFilled` 原始 path，单色用 `currentColor` 上品牌粉。
 *
 * ⚠ 与同文件里的 `IconHeart`（24 视框、1.6px 描边）是两个东西，别互相替换：
 * 那个用在 `/relationship` 与关系徽标里，是正文语境，实心会太重。
 *
 * ⚠ 也与 `IconHeartOutline` 不同：那个名字里带 Outline 但也用 `fill`，
 * 靠 path 自带的内轮廓挖空成心形圆环（antd 的 `HeartOutlined`），
 * 用在右上角图标栏和「点击查看…」按钮上。三个心各有各的位置。
 *
 * antd 的 viewBox 是 `64 64 896 896`（不是 `0 0 1024 1024`），照抄，
 * 换成后者会让图形偏移并被裁掉一圈。
 */
const filledInset = (size: number) => ({
  width: size,
  height: size,
  viewBox: "64 64 896 896",
  fill: "currentColor",
  "aria-hidden": true,
  focusable: false as const,
});

/** 好感度。实心心，对应旧版 `HeartTwoTone`。 */
export function IconHeartSolid({ size = 15, className }: IconProps) {
  return (
    <svg {...filledInset(size)} className={className}>
      <path d="M923 283.6a260.04 260.04 0 00-56.9-82.8 264.4 264.4 0 00-84-55.5A265.34 265.34 0 00679.7 125c-49.3 0-97.4 13.5-139.2 39-10 6.1-19.5 12.8-28.5 20.1-9-7.3-18.5-14-28.5-20.1-41.8-25.5-89.9-39-139.2-39-35.5 0-69.9 6.8-102.4 20.3-31.4 13-59.7 31.7-84 55.5a258.44 258.44 0 00-56.9 82.8c-13.9 32.3-21 66.6-21 101.9 0 33.3 6.8 68 20.3 103.3 11.3 29.5 27.5 60.1 48.2 91 32.8 48.9 77.9 99.9 133.9 151.6 92.8 85.7 184.7 144.9 188.6 147.3l23.7 15.2c10.5 6.7 24 6.7 34.5 0l23.7-15.2c3.9-2.5 95.7-61.6 188.6-147.3 56-51.7 101.1-102.7 133.9-151.6 20.7-30.9 37-61.5 48.2-91 13.5-35.3 20.3-70 20.3-103.3.1-35.3-7-69.6-20.9-101.9z" />
    </svg>
  );
}

/**
 * 实心火焰的路径与视框。灵力，对应旧版 `FireTwoTone`——旧版用的是火不是水滴。
 *
 * 导出的是**路径而不是一个 `IconFireSolid` 组件**：唯一的用处是
 * `ValueHud` 的 `PowerFlame`，而它要把同一条形状画两遍（一遍当空槽、
 * 一遍用 clipPath 裁成按比例填充），拿现成的 `<svg>` 组件包不出来。
 * 再留一个没人调的组件只是噪音；真需要一枚整只的火焰图标时，
 * 照着别的图标包一层就是了，几何仍然只有这一份。
 */
export const FILLED_VIEWBOX = "64 64 896 896";
export const FIRE_SOLID_PATH =
  "M834.1 469.2A347.49 347.49 0 00751.2 354l-29.1-26.7a8.09 8.09 0 00-13 3.3l-13 37.3c-8.1 23.4-23 47.3-44.1 70.8-1.4 1.5-3 1.9-4.1 2-1.1.1-2.8-.1-4.3-1.5-1.4-1.2-2.1-3-2-4.8 3.7-60.2-14.3-128.1-53.7-202C555.3 171 510 123.1 453.4 89.7l-41.3-24.3c-5.4-3.2-12.3 1-12 7.3l2.2 48c1.5 32.8-2.3 61.8-11.3 85.9-11 29.5-26.8 56.9-47 81.5a295.64 295.64 0 01-47.5 46.1 352.6 352.6 0 00-100.3 121.5A347.75 347.75 0 00160 610c0 47.2 9.3 92.9 27.7 136a349.4 349.4 0 0075.5 110.9c32.4 32 70 57.2 111.9 74.7C418.5 949.8 464.5 959 512 959s93.5-9.2 136.9-27.3A348.6 348.6 0 00760.8 857c32.4-32 57.8-69.4 75.5-110.9a344.2 344.2 0 0027.7-136c0-48.8-10-96.2-29.9-140.9z";

/** 收藏 / 羁绊。 */
export function IconHeartOutline({ size = 18, className }: IconProps) {
  return (
    <svg {...filled(size)} className={className}>
      <path d="M923 283.6a260.04 260.04 0 00-56.9-82.8 264.4 264.4 0 00-84-55.5A265.34 265.34 0 00679.7 125c-49.3 0-97.4 13.5-139.2 39-10 6.1-19.5 12.8-28.5 20.1-9-7.3-18.5-14-28.5-20.1-41.8-25.5-89.9-39-139.2-39-35.5 0-69.9 6.8-102.4 20.3-31.4 13-59.7 31.7-84 55.5a258.44 258.44 0 00-56.9 82.8c-13.9 32.3-21 66.6-21 101.9 0 33.3 6.8 68 20.3 103.3 11.3 29.5 27.5 60.1 48.2 91 32.8 48.9 77.9 99.9 133.9 151.6 92.8 85.7 184.7 144.9 188.6 147.3l23.7 15.2c10.5 6.7 24 6.7 34.5 0l23.7-15.2c3.9-2.5 95.7-61.6 188.6-147.3 56-51.7 101.1-102.7 133.9-151.6 20.7-30.9 37-61.5 48.2-91 13.5-35.3 20.3-70 20.3-103.3.1-35.3-7-69.6-20.9-101.9zM512 814.8S156 586.7 156 385.5C156 283.6 240.3 201 344.3 201c73.1 0 136.5 40.8 167.7 100.4C543.2 241.8 606.6 201 679.7 201c104 0 188.3 82.6 188.3 184.5 0 201.2-356 429.3-356 429.3z" />
    </svg>
  );
}

/** 模式切换 · 当前是视觉小说模式，点了切到气泡。 */
export function IconComment({ size = 18, className }: IconProps) {
  return (
    <svg {...filled(size)} className={className}>
      <path d="M573 421c-23.1 0-41 17.9-41 40s17.9 40 41 40c21.1 0 39-17.9 39-40s-17.9-40-39-40zm-280 0c-23.1 0-41 17.9-41 40s17.9 40 41 40c21.1 0 39-17.9 39-40s-17.9-40-39-40z" />
      <path d="M894 345a343.92 343.92 0 00-189-130v.1c-17.1-19-36.4-36.5-58-52.1-163.7-119-393.5-82.7-513 81-96.3 133-92.2 311.9 6 439l.8 132.6c0 3.2.5 6.4 1.5 9.4a31.95 31.95 0 0040.1 20.9L309 806c33.5 11.9 68.1 18.7 102.5 20.6l-.5.4c89.1 64.9 205.9 84.4 313 49l127.1 41.4c3.2 1 6.5 1.6 9.9 1.6 17.7 0 32-14.3 32-32V753c88.1-119.6 90.4-284.9 1-408zM323 735l-12-5-99 31-1-104-8-9c-84.6-103.2-90.2-251.9-11-361 96.4-132.2 281.2-161.4 413-66 132.2 96.1 161.5 280.6 66 412-80.1 109.9-223.5 150.5-348 102zm505-17l-8 10 1 104-98-33-12 5c-56 20.8-115.7 22.5-171 7l-.2-.1A367.31 367.31 0 00729 676c76.4-105.3 88.8-237.6 44.4-350.4l.6.4c23 16.5 44.1 37.1 62 62 72.6 99.6 68.5 235.2-8 330z" />
      <path d="M433 421c-23.1 0-41 17.9-41 40s17.9 40 41 40c21.1 0 39-17.9 39-40s-17.9-40-39-40z" />
    </svg>
  );
}

/** 模式切换 · 当前是气泡模式，点了切回视觉小说。 */
export function IconSolution({ size = 18, className }: IconProps) {
  return (
    <svg {...filled(size)} className={className}>
      <path d="M688 264c0-4.4-3.6-8-8-8H296c-4.4 0-8 3.6-8 8v48c0 4.4 3.6 8 8 8h384c4.4 0 8-3.6 8-8v-48zm-8 136H296c-4.4 0-8 3.6-8 8v48c0 4.4 3.6 8 8 8h384c4.4 0 8-3.6 8-8v-48c0-4.4-3.6-8-8-8zM480 544H296c-4.4 0-8 3.6-8 8v48c0 4.4 3.6 8 8 8h184c4.4 0 8-3.6 8-8v-48c0-4.4-3.6-8-8-8zm-48 308H208V148h560v344c0 4.4 3.6 8 8 8h56c4.4 0 8-3.6 8-8V108c0-17.7-14.3-32-32-32H168c-17.7 0-32 14.3-32 32v784c0 17.7 14.3 32 32 32h264c4.4 0 8-3.6 8-8v-56c0-4.4-3.6-8-8-8zm356.8-74.4c29-26.3 47.2-64.3 47.2-106.6 0-79.5-64.5-144-144-144s-144 64.5-144 144c0 42.3 18.2 80.3 47.2 106.6-57 32.5-96.2 92.7-99.2 162.1-.2 4.5 3.5 8.3 8 8.3h48.1c4.2 0 7.7-3.3 8-7.6C564 871.2 621.7 816 692 816s128 55.2 131.9 124.4c.2 4.2 3.7 7.6 8 7.6H880c4.6 0 8.2-3.8 8-8.3-2.9-69.5-42.2-129.6-99.2-162.1zM692 591c44.2 0 80 35.8 80 80s-35.8 80-80 80-80-35.8-80-80 35.8-80 80-80z" />
    </svg>
  );
}

export function IconPencil({ size = 20, className }: IconProps) {
  return (
    <svg {...base(size)} className={className}>
      <path d="M4 20.1 4.7 16l10-10a2.1 2.1 0 0 1 3 3l-10 10z" />
      <path d="m13.4 7.3 3.3 3.3" />
    </svg>
  );
}

/** 复制。两张叠起来的纸。 */
export function IconCopy({ size = 20, className }: IconProps) {
  return (
    <svg {...base(size)} className={className}>
      <rect x="9" y="9" width="11" height="11" rx="2.4" />
      <path d="M15 5.6A2.6 2.6 0 0 0 12.4 3H6.6A2.6 2.6 0 0 0 4 5.6v5.8A2.6 2.6 0 0 0 6.6 14" />
    </svg>
  );
}

/**
 * 赞 / 踩。
 *
 * 同一条路径上下翻转，不是两张各画各的图——分开画的话线宽与重心永远对不齐，
 * 并排摆在一起一眼就能看出来。旧项目正是同时用了 antd 的 LikeOutlined 与
 * antd-mobile 的图标，两套视觉重心不同。
 */
export function IconThumbUp({ size = 20, className }: IconProps) {
  return (
    <svg {...base(size)} className={className}>
      <path d="M7 10.5v9.2H4.6A1.6 1.6 0 0 1 3 18.1v-6A1.6 1.6 0 0 1 4.6 10.5z" />
      <path d="M7 10.5 11.4 3a2.3 2.3 0 0 1 2.2 2.9l-.8 3.2h5.3a2 2 0 0 1 2 2.5l-1.5 6a2 2 0 0 1-2 1.6H7z" />
    </svg>
  );
}

export function IconThumbDown({ size = 20, className }: IconProps) {
  return (
    <svg {...base(size)} className={className}>
      <path d="M7 13.5V4.3H4.6A1.6 1.6 0 0 0 3 5.9v6a1.6 1.6 0 0 0 1.6 1.6z" />
      <path d="M7 13.5 11.4 21a2.3 2.3 0 0 0 2.2-2.9l-.8-3.2h5.3a2 2 0 0 0 2-2.5l-1.5-6a2 2 0 0 0-2-1.6H7z" />
    </svg>
  );
}

/** 删除。 */
export function IconTrash({ size = 20, className }: IconProps) {
  return (
    <svg {...base(size)} className={className}>
      <path d="M4 6.6h16" />
      <path d="M9.2 6.6V4.8a1.4 1.4 0 0 1 1.4-1.4h2.8a1.4 1.4 0 0 1 1.4 1.4v1.8" />
      <path d="M6.2 6.6 7 19.2a1.8 1.8 0 0 0 1.8 1.7h6.4a1.8 1.8 0 0 0 1.8-1.7l.8-12.6" />
    </svg>
  );
}

/**
 * 喇叭 · 静音。默认态：这条消息还没生成过语音。
 *
 * 三档共用同一个喇叭轮廓，只换右侧的声波/斜杠，切换时形状不跳。
 */
export function IconSpeakerMuted({ size = 20, className }: IconProps) {
  return (
    <svg {...base(size)} className={className}>
      <path d="M4 9.5h3.2L11.5 6v12L7.2 14.5H4z" />
      <path d="m16 9.5 4 5" />
      <path d="m20 9.5-4 5" />
    </svg>
  );
}

/** 喇叭 · 可播放。已经有音频，点一下就响。 */
export function IconSpeaker({ size = 20, className }: IconProps) {
  return (
    <svg {...base(size)} className={className}>
      <path d="M4 9.5h3.2L11.5 6v12L7.2 14.5H4z" />
      <path d="M15 9.8a3.4 3.4 0 0 1 0 4.4" />
    </svg>
  );
}

/**
 * 喇叭 · 正在播放。两道声波，由 CSS 驱动依次淡入淡出。
 *
 * 动效放在 CSS 而不是这里：prefers-reduced-motion 要能一键关掉，
 * 见 components.css 的 .speak__wave。
 */
export function IconSpeakerPlaying({ size = 20, className }: IconProps) {
  return (
    <svg {...base(size)} className={className}>
      <path d="M4 9.5h3.2L11.5 6v12L7.2 14.5H4z" />
      <path className="speak__wave speak__wave--1" d="M15 9.8a3.4 3.4 0 0 1 0 4.4" />
      <path className="speak__wave speak__wave--2" d="M17.8 7.4a7 7 0 0 1 0 9.2" />
    </svg>
  );
}
